import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, LogOut, RefreshCw, Loader2, Truck, CheckCircle2, Banknote, Package, Home, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";
import { fmtPrice } from "@/lib/format";

const TOKEN_KEY = "ng_admin_token";

const STATUS_STYLE = {
  en_attente_virement: "border-amber-400/40 bg-amber-500/10 text-amber-300",
  en_attente_paiement: "border-slate-500/40 bg-slate-500/10 text-slate-300",
  payee: "border-emerald-400/40 bg-emerald-500/10 text-emerald-300",
  expediee: "border-sky-400/40 bg-sky-500/10 text-sky-300",
  livree: "border-emerald-400/40 bg-emerald-500/15 text-emerald-200",
};

const METHOD_LABEL = { interac: "Interac", paypal: "PayPal", card: "Carte", applepay: "Apple Pay" };

export default function Admin() {
  const { t, lang } = useLang();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tracking, setTracking] = useState({});

  const load = useCallback(async (tk = token) => {
    try {
      const r = await api.get("/admin/orders", { headers: { Authorization: `Bearer ${tk}` } });
      setOrders(r.data.orders);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setOrders(null);
      } else {
        toast.error(t("checkout.errPay"));
      }
    }
  }, [token, t]);

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      setPassword("");
    } catch (e) {
      toast.error(e.response?.status === 429 ? e.response.data.detail : t("admin.wrong"));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (orderId, status) => {
    try {
      await api.post(
        `/admin/orders/${orderId}/status`,
        { status, tracking_number: tracking[orderId] || "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("admin.shippedNote"));
      load();
    } catch {
      toast.error(t("checkout.errPay"));
    }
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });

  return (
    <main className="mx-auto max-w-4xl px-4 pt-32 pb-24 sm:px-8" data-testid="admin-page">
      <Seo title="Admin | NordiGuard" description="Espace privé NordiGuard" />
      {!token ? (
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="glacial-card mx-auto mt-16 max-w-sm p-8"
          data-testid="admin-login-form"
        >
          <div className="flex flex-col items-center text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-500/15 text-sky-400">
              <ShieldCheck size={22} />
            </span>
            <h1 className="font-display mt-4 text-xl font-extrabold text-slate-50">{t("admin.title")}</h1>
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-500">{t("admin.subtitle")}</p>
          </div>
          <div className="relative mt-7">
            <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("admin.password")}
              className="w-full rounded-xl border border-slate-700 bg-slate-900/60 py-3 pl-11 pr-4 text-sm text-slate-100 placeholder-slate-500 outline-none transition-colors focus:border-sky-400"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !password}
            data-testid="admin-login-button"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-sky-400 py-3 text-sm font-extrabold text-slate-950 transition-all duration-300 hover:bg-sky-300 disabled:opacity-60"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : null}
            {t("admin.login")}
          </button>
        </motion.form>
      ) : (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl font-extrabold text-slate-50">{t("admin.title")}</h1>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-500">{t("admin.subtitle")}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => load()}
                data-testid="admin-refresh-button"
                className="flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-xs font-bold text-slate-300 transition-colors hover:border-sky-400 hover:text-sky-300"
              >
                <RefreshCw size={13} /> {t("admin.refresh")}
              </button>
              <button
                onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setOrders(null); }}
                data-testid="admin-logout-button"
                className="flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-xs font-bold text-slate-300 transition-colors hover:border-red-400 hover:text-red-300"
              >
                <LogOut size={13} /> {t("admin.logout")}
              </button>
            </div>
          </div>

          {orders === null ? (
            <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-sky-400" /></div>
          ) : orders.length === 0 ? (
            <p className="py-20 text-center text-sm text-slate-400" data-testid="admin-empty">{t("admin.empty")}</p>
          ) : (
            <div className="mt-8 space-y-4" data-testid="admin-orders-list">
              {orders.map((o) => (
                <div key={o.id} className="glacial-card p-5" data-testid={`admin-order-${o.confirmation_code}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-mono text-sm font-extrabold tracking-widest text-sky-300">{o.confirmation_code}</span>
                        <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[o.status] || STATUS_STYLE.en_attente_paiement}`}
                          data-testid={`admin-status-${o.confirmation_code}`}>
                          {t(`admin.status.${o.status}`)}
                        </span>
                        <span className="text-[11px] font-semibold text-slate-500">{METHOD_LABEL[o.payment_method] || o.payment_method}</span>
                      </div>
                      <p className="mt-2 text-sm font-bold text-slate-100">{o.name} — <span className="font-semibold text-slate-300">{o.vehicle}</span></p>
                      <p className="mt-0.5 text-xs text-slate-400">{o.email} · {o.phone}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{o.address}</p>
                      {o.tracking_number && (
                        <p className="mt-1 text-xs font-semibold text-sky-300">
                          {t("success.tracking") || "Suivi"} : {o.tracking_number}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-display text-xl font-extrabold text-slate-50">{fmtPrice(o.total, lang)}</p>
                      <p className="text-xs text-slate-500">{fmtDate(o.created_at)}</p>
                      <p className="text-xs text-slate-500">×{o.quantity}</p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-800 pt-4">
                    {o.status === "en_attente_virement" && (
                      <button
                        onClick={() => setStatus(o.id, "payee")}
                        data-testid={`admin-paid-${o.confirmation_code}`}
                        className="flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-4 py-2 text-xs font-bold text-emerald-300 transition-colors hover:bg-emerald-500/25"
                      >
                        <Banknote size={13} /> {t("admin.actPaid")}
                      </button>
                    )}
                    {o.status === "payee" && (
                      <>
                        <input
                          value={tracking[o.id] || ""}
                          onChange={(e) => setTracking({ ...tracking, [o.id]: e.target.value })}
                          placeholder={t("admin.trackingPlaceholder")}
                          data-testid={`admin-tracking-${o.confirmation_code}`}
                          className="w-48 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-2 text-xs text-slate-100 placeholder-slate-500 outline-none focus:border-sky-400"
                        />
                        <button
                          onClick={() => setStatus(o.id, "expediee")}
                          data-testid={`admin-ship-${o.confirmation_code}`}
                          className="flex items-center gap-1.5 rounded-full bg-sky-500/15 px-4 py-2 text-xs font-bold text-sky-300 transition-colors hover:bg-sky-500/25"
                        >
                          <Truck size={13} /> {t("admin.actShip")}
                        </button>
                      </>
                    )}
                    {o.status === "expediee" && (
                      <button
                        onClick={() => setStatus(o.id, "livree")}
                        data-testid={`admin-deliver-${o.confirmation_code}`}
                        className="flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-4 py-2 text-xs font-bold text-emerald-300 transition-colors hover:bg-emerald-500/25"
                      >
                        <CheckCircle2 size={13} /> {t("admin.actDeliver")}
                      </button>
                    )}
                    {(o.status === "livree" || o.status === "expediee") && (
                      <span className="flex items-center gap-1.5 text-xs text-slate-500">
                        {o.status === "livree" ? <Home size={13} /> : <Package size={13} />}
                        {t(`admin.status.${o.status}`)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </main>
  );
}
